(() => {
  'use strict';
  if (window.scCommerceInitialized) return;
  window.scCommerceInitialized = true;
  document.documentElement.classList.replace('no-js', 'js');
  const labelNode = document.getElementById('sc-commerce-labels');
  let labels;
  try { labels = JSON.parse(labelNode?.textContent || '{}'); } catch { return; }
  const root = () => window.Shopify?.routes?.root || document.body.dataset.routesRoot || '/';
  const endpoint = (path) => `${root().replace(/\/?$/, '/')}${path}`;
  const previewThemeId = /^\d+$/.test(document.body.dataset.previewTheme || '') ? document.body.dataset.previewTheme : '';
  const previewUrl = (raw) => {
    if (!previewThemeId || !raw || raw.startsWith('#')) return raw;
    try {
      const url = new URL(raw, window.location.href);
      if (url.origin !== window.location.origin || !['http:', 'https:'].includes(url.protocol)) return raw;
      if (/\/(?:checkouts?|account|customer_authentication|authentication|admin|cdn)(?:\/|$)/i.test(url.pathname) || /\/cart\/(?:c(?:\/|$)|\d)/i.test(url.pathname)) return raw;
      if (url.searchParams.has('preview_theme_id') && url.searchParams.get('preview_theme_id') !== previewThemeId) return raw;
      url.searchParams.set('preview_theme_id', previewThemeId);
      return url.href;
    } catch { return raw; }
  };
  const preservePreview = (scope = document) => {
    if (!previewThemeId) return;
    scope.querySelectorAll('a[href]:not([download])').forEach(link => { link.href = previewUrl(link.getAttribute('href')); });
    scope.querySelectorAll('form').forEach(form => {
      if (form.method.toLowerCase() !== 'get') return;
      const action = new URL(previewUrl(form.action), window.location.href);
      if (action.origin !== window.location.origin || action.searchParams.get('preview_theme_id') !== previewThemeId) return;
      if (/\/(?:checkouts?|account|customer_authentication|authentication|admin|cdn)(?:\/|$)/i.test(action.pathname)) return;
      if (form.querySelector('[name="preview_theme_id"]')) return;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'preview_theme_id';
      input.value = previewThemeId;
      form.append(input);
    });
  };
  const dialog = () => document.querySelector('[data-cart-dialog]');
  let busy = false;
  let previousFocus = null;
  let cartCurrency = document.body.dataset.currency || 'USD';
  let openRequest = 0;
  let lastCart = null;
  const q = (selector, scope = document) => scope.querySelector(selector);
  const qa = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const translated = (key, value) => String(labels[key] || '').replace('[title]', value).replace('[count]', value);
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: cartCurrency }).format(Number(cents || 0) / 100);
  const node = (tag, className, text) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  };
  const safeUrl = (raw, sameOrigin = false) => {
    if (!raw) return '';
    try {
      const url = new URL(raw, window.location.origin);
      if (!['https:', 'http:'].includes(url.protocol)) return '';
      if (sameOrigin && url.origin !== window.location.origin) return '';
      return url.href;
    } catch { return ''; }
  };
  const arrow = () => {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', '22');
    svg.setAttribute('height', '22');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.5');
    const path = document.createElementNS(svg.namespaceURI, 'path');
    path.setAttribute('d', 'M4 12h16m-6-6 6 6-6 6');
    svg.append(path);
    return svg;
  };
  const showMessage = (selector, text, scope = document) => {
    qa(selector, scope).forEach(element => {
      element.textContent = text || '';
      if (element.hasAttribute('hidden') || selector.includes('error')) element.hidden = !text;
    });
  };
  const request = async (path, options = {}) => {
    const response = await fetch(endpoint(path), { credentials: 'same-origin', cache: 'no-store', ...options, headers: { Accept: 'application/json', ...options.headers } });
    let data;
    try { data = await response.json(); } catch { throw new Error(labels.error); }
    if (!response.ok) {
      const message = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : labels.error;
      throw new Error(message);
    }
    return data;
  };
  const setBusy = (state) => {
    busy = state;
    qa('[data-product-add], [data-cart-quantity], [data-cart-step], [data-cart-remove], .sc-cart-form [name="checkout"], [data-cart-update]').forEach(element => {
      if (state) {
        element.dataset.wasDisabled = element.disabled ? 'true' : 'false';
        element.disabled = true;
        element.setAttribute('aria-disabled', 'true');
      } else {
        if (Object.hasOwn(element.dataset, 'wasDisabled')) element.disabled = element.dataset.wasDisabled === 'true';
        element.removeAttribute('aria-disabled');
        delete element.dataset.wasDisabled;
      }
    });
    qa('.sc-cart-form').forEach(form => form.setAttribute('aria-busy', String(state)));
  };
  const updateCounts = (cart) => {
    qa('[data-cart-count]').forEach(element => { element.textContent = cart.item_count; element.hidden = cart.item_count === 0; });
    qa('[data-cart-open]').forEach(element => element.setAttribute('aria-label', translated('bagCount', String(cart.item_count))));
  };
  const quantityRules = (item) => {
    const rule = item.quantity_rule || item.variant?.quantity_rule || {};
    return { min: Math.max(1, Number(rule.min) || 1), max: Number(rule.max) > 0 ? Number(rule.max) : null, step: Math.max(1, Number(rule.increment) || 1) };
  };
  const renderCart = (cart, focusState) => {
    lastCart = cart;
    cartCurrency = cart.currency || document.body.dataset.currency || 'USD';
    updateCounts(cart);
    qa('[data-cart-content]').forEach((container, containerIndex) => {
      const fragment = document.createDocumentFragment();
      if (!cart.items.length) {
        const empty = node('div', 'empty-bag');
        empty.append(node('h3', '', labels.emptyTitle), node('p', '', labels.empty));
        const link = node('a', 'button rust', labels.explore);
        link.href = previewUrl(safeUrl(labels.shopUrl, true) || endpoint('collections/all'));
        link.append(arrow());
        empty.append(link);
        fragment.append(empty);
      } else {
        const form = node('form', 'sc-cart-form');
        form.method = 'post';
        form.action = endpoint('cart');
        form.dataset.nativeCartForm = '';
        const items = node('div', 'bag-items');
        cart.items.forEach((item, index) => {
          const rule = quantityRules(item);
          const article = node('article', 'bag-item');
          article.dataset.cartItem = '';
          article.dataset.itemKey = item.key;
          const imageUrl = safeUrl(item.featured_image?.url || item.image || '');
          if (imageUrl) {
            const image = node('img');
            image.src = imageUrl;
            image.alt = item.featured_image?.alt || item.product_title || item.title;
            image.width = 105;
            image.height = 129;
            image.loading = 'lazy';
            article.append(image);
          }
          const detail = node('div');
          const title = node('h3');
          const productLink = node('a', '', item.product_title || item.title);
          productLink.href = previewUrl(safeUrl(item.url, true) || endpoint('cart'));
          title.append(productLink);
          detail.append(title);
          if (item.variant_title && item.variant_title !== 'Default Title') detail.append(node('p', '', item.variant_title));
          if (item.selling_plan_allocation?.selling_plan?.name) detail.append(node('p', '', item.selling_plan_allocation.selling_plan.name));
          Object.entries(item.properties || {}).forEach(([key, value]) => { if (!key.startsWith('_') && value !== '' && value !== null) detail.append(node('p', '', `${key}: ${String(value)}`)); });
          const price = node('p', 'bag-line-price');
          if (item.original_line_price > item.final_line_price) price.append(node('s', '', money(item.original_line_price)), document.createTextNode(' '));
          price.append(document.createTextNode(money(item.final_line_price)));
          detail.append(price);
          (item.line_level_discount_allocations || []).forEach(allocation => detail.append(node('p', '', `${allocation.discount_application.title} (−${money(allocation.amount)})`)));
          const controls = node('div', 'bag-item-controls');
          const quantity = node('div', 'quantity-control is-enhanced');
          quantity.dataset.quantityControl = '';
          const minus = node('button', '', '−');
          minus.type = 'button';
          minus.dataset.cartStep = '-1';
          minus.setAttribute('aria-label', translated('decrease', item.product_title || item.title));
          const input = node('input');
          input.type = 'number';
          input.name = 'updates[]';
          input.value = item.quantity;
          input.defaultValue = item.quantity;
          input.min = '0';
          input.step = rule.step;
          if (rule.max) input.max = rule.max;
          input.required = true;
          input.inputMode = 'numeric';
          input.id = `sc-bag-quantity-${containerIndex}-${index}`;
          input.dataset.cartQuantity = '';
          input.dataset.itemKey = item.key;
          input.dataset.ruleMin = rule.min;
          const inputLabel = node('label', 'visually-hidden', translated('quantity', item.product_title || item.title));
          inputLabel.htmlFor = input.id;
          const plus = node('button', '', '+');
          plus.type = 'button';
          plus.dataset.cartStep = '1';
          plus.setAttribute('aria-label', translated('increase', item.product_title || item.title));
          plus.disabled = rule.max !== null && item.quantity >= rule.max;
          quantity.append(minus, inputLabel, input, plus);
          const remove = node('button', 'remove-button', labels.remove);
          remove.type = 'button';
          remove.dataset.cartRemove = '';
          controls.append(quantity, remove);
          detail.append(controls);
          article.append(detail);
          items.append(article);
        });
        const total = node('div', 'bag-total');
        (cart.cart_level_discount_applications || []).forEach(discount => {
          const row = node('div', 'cart-discount');
          row.append(node('span', '', discount.title), node('span', '', `−${money(discount.total_allocated_amount)}`));
          total.append(row);
        });
        const subtotal = node('div');
        subtotal.append(node('span', '', labels.subtotal), node('strong', '', money(cart.total_price)));
        total.append(subtotal, node('p', '', labels.shipping));
        const checkout = node('button', 'button rust', labels.checkout);
        checkout.type = 'submit';
        checkout.name = 'checkout';
        checkout.append(arrow());
        total.append(checkout, node('span', 'checkout-note', labels.checkoutNote));
        form.append(items, total);
        fragment.append(form);
      }
      container.replaceChildren(fragment);
    });
    if (focusState) {
      const scope = focusState.drawer ? dialog() : q('[data-cart-page]');
      const row = qa('[data-cart-item]', scope || document).find(item => item.dataset.itemKey === focusState.key);
      const focusTarget = row && q(focusState.selector, row);
      (focusTarget || q('[data-cart-close]', scope || document) || q('[name="checkout"]', scope || document) || q('a', scope || document))?.focus();
    }
  };
  const getCart = async (focusState) => { const cart = await request('cart.js'); renderCart(cart, focusState); return cart; };
  const openCart = async (trigger) => {
    const drawer = dialog();
    if (!drawer?.showModal) { window.location.assign(previewUrl(endpoint('cart'))); return; }
    if (!drawer.open) {
      previousFocus = trigger || document.activeElement;
      drawer.showModal();
      document.documentElement.classList.add('sc-cart-open');
    }
    const requestId = ++openRequest;
    showMessage('[data-cart-error]', '');
    showMessage('[data-cart-status]', labels.loading, drawer);
    setBusy(true);
    try { await getCart(); } catch (error) { if (requestId === openRequest) showMessage('[data-cart-error]', error.message || labels.error, drawer); }
    finally { setBusy(false); if (requestId === openRequest) showMessage('[data-cart-status]', '', drawer); }
  };
  const closeCart = () => { const drawer = dialog(); if (drawer?.open) drawer.close(); };
  const changeItem = async (key, quantity, trigger) => {
    if (busy || !Number.isInteger(quantity) || quantity < 0) return;
    const focusState = { key, drawer: !!trigger.closest('[data-cart-dialog]'), selector: trigger.matches('[data-cart-step]') ? `[data-cart-step="${trigger.dataset.cartStep}"]` : trigger.matches('[data-cart-remove]') ? '[data-cart-remove]' : '[data-cart-quantity]' };
    showMessage('[data-cart-error]', '');
    showMessage('[data-cart-status]', labels.updating);
    setBusy(true);
    let errorMessage = '';
    let cart;
    try { cart = await request('cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: key, quantity }) }); }
    catch (error) { errorMessage = error.message || labels.error; try { cart = await request('cart.js'); } catch {} }
    finally {
      setBusy(false);
      if (cart) renderCart(cart, focusState);
      else if (lastCart) renderCart(lastCart, focusState);
      showMessage('[data-cart-status]', errorMessage ? '' : labels.updated);
      if (errorMessage) showMessage('[data-cart-error]', errorMessage);
    }
  };
  const syncProduct = (section, updateUrl = false) => {
    const select = q('[data-product-variant]', section);
    const option = select?.selectedOptions[0];
    if (!option) return;
    const available = option.dataset.available === 'true';
    const add = q('[data-product-add]', section);
    if (add) add.disabled = !available || busy;
    q('[data-product-add-label]', section).textContent = available ? labels.add : labels.soldOut;
    q('[data-product-price]', section).textContent = option.dataset.price || '';
    const input = q('[data-product-quantity]', section);
    const min = Math.max(1, Number(option.dataset.quantityMin) || 1);
    const max = Number(option.dataset.quantityMax) || null;
    const step = Math.max(1, Number(option.dataset.quantityStep) || 1);
    input.min = min;
    input.step = step;
    if (max) input.max = max; else input.removeAttribute('max');
    if (updateUrl) input.value = min;
    syncQuantity(input);
    if (updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', select.value);
      window.history.replaceState({}, '', url);
      const image = qa('[data-product-thumbnail]', section).find(link => link.dataset.imageId === option.dataset.imageId);
      if (image) selectImage(image);
      showMessage('[data-product-error]', '', section);
      showMessage('[data-product-status]', '', section);
    }
  };
  const syncQuantity = (input) => {
    const group = input.closest('[data-quantity-control]');
    if (!group) return;
    const minus = q('[data-quantity-minus]', group);
    const plus = q('[data-quantity-plus]', group);
    if (minus) minus.disabled = Number(input.value) <= Number(input.min || 1);
    if (plus) plus.disabled = !!input.max && Number(input.value) >= Number(input.max);
  };
  const selectImage = (link) => {
    const gallery = link.closest('.product-gallery');
    const image = q('[data-product-main-image]', gallery);
    const source = safeUrl(link.dataset.imageSrc);
    if (!image || !source) return;
    image.src = source;
    image.removeAttribute('srcset');
    image.alt = link.dataset.imageAlt || '';
    qa('[data-product-thumbnail]', gallery).forEach(thumbnail => {
      const current = thumbnail === link;
      thumbnail.classList.toggle('selected', current);
      if (current) thumbnail.setAttribute('aria-current', 'true'); else thumbnail.removeAttribute('aria-current');
    });
  };
  const init = (scope = document) => {
    preservePreview(scope);
    qa('[data-quantity-control]', scope).forEach(control => { control.classList.add('is-enhanced'); qa('button[hidden]', control).forEach(button => { button.hidden = false; }); });
    qa('[data-product-section]', scope).forEach(section => syncProduct(section));
    qa('[data-cart-update]', scope).forEach(button => { button.hidden = true; });
  };
  document.addEventListener('submit', async event => {
    if (event.target.matches('[data-native-cart-form]') && busy) { event.preventDefault(); return; }
    const form = event.target.closest('[data-product-form]');
    if (!form || !window.fetch || !window.FormData) return;
    event.preventDefault();
    if (busy || !form.reportValidity()) return;
    const section = form.closest('[data-product-section]');
    if (q('[data-product-variant]', form)?.selectedOptions[0]?.dataset.available !== 'true') return;
    const data = new FormData(form);
    showMessage('[data-product-error]', '', form);
    showMessage('[data-product-status]', '', form);
    setBusy(true);
    q('[data-product-add-label]', form).textContent = labels.adding;
    let added = false;
    try {
      await request('cart/add.js', { method: 'POST', body: data });
      added = true;
      await getCart();
      showMessage('[data-product-status]', labels.added, form);
    } catch (error) {
      if (added) { window.location.assign(previewUrl(endpoint('cart'))); return; }
      showMessage('[data-product-error]', error.message || labels.addError, form);
      try { await getCart(); } catch {}
    } finally { setBusy(false); syncProduct(section); }
    if (added) await openCart(q('[data-product-add]', form));
  });
  document.addEventListener('click', event => {
    const target = event.target.closest('a,button');
    if (!target) return;
    if (target.matches('a[href]:not([download])')) target.href = previewUrl(target.getAttribute('href'));
    if (target.matches('[data-cart-open]')) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      if (!dialog()?.showModal || !window.fetch) return;
      event.preventDefault();
      if (!busy) openCart(target);
    } else if (target.matches('[data-cart-close]')) closeCart();
    else if (target.matches('[data-cart-step], [data-cart-remove]')) {
      event.preventDefault();
      if (busy) return;
      const row = target.closest('[data-cart-item]');
      const input = q('[data-cart-quantity]', row);
      let quantity = 0;
      if (!target.matches('[data-cart-remove]')) {
        const min = Number(input.dataset.ruleMin) || 1;
        const step = Number(input.step) || 1;
        quantity = Number(input.value) + Number(target.dataset.cartStep) * step;
        if (quantity < min) quantity = target.dataset.cartStep === '-1' ? 0 : min;
        if (input.max) quantity = Math.min(quantity, Number(input.max));
      }
      changeItem(row.dataset.itemKey, quantity, target);
    } else if (target.matches('[data-quantity-minus], [data-quantity-plus]')) {
      const input = q('input', target.closest('[data-quantity-control]'));
      if (target.matches('[data-quantity-minus]')) input.stepDown(); else input.stepUp();
      syncQuantity(input);
    } else if (target.matches('[data-product-thumbnail]')) {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
      event.preventDefault();
      selectImage(target);
    } else if (target.matches('[data-menu-toggle]')) {
      const nav = q('[data-menu-nav]');
      const open = target.getAttribute('aria-expanded') !== 'true';
      target.setAttribute('aria-expanded', String(open));
      target.setAttribute('aria-label', open ? target.dataset.menuCloseLabel : target.dataset.menuOpenLabel);
      nav?.classList.toggle('open', open);
      const openIcon = q('[data-menu-icon-open]', target), closeIcon = q('[data-menu-icon-close]', target);
      if (openIcon) openIcon.hidden = open;
      if (closeIcon) closeIcon.hidden = !open;
    } else if (target.matches('[data-music-open]')) {
      const player = q('[data-music-player]');
      const frame = q('[data-music-frame]', player || document);
      if (!player || !frame) return;
      if (!frame.hasAttribute('src')) frame.src = frame.dataset.src;
      player.hidden = false;
      target.setAttribute('aria-expanded', 'true');
      player.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
    } else if (target.matches('[data-music-close]')) {
      const player = q('[data-music-player]');
      if (player) { player.hidden = true; q('[data-music-frame]', player)?.removeAttribute('src'); }
      const trigger = q('[data-music-open]');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.focus();
    }
  });
  document.addEventListener('change', event => {
    const target = event.target;
    if (target.matches('[data-product-variant]')) syncProduct(target.closest('[data-product-section]'), true);
    else if (target.matches('[data-product-quantity]')) syncQuantity(target);
    else if (target.matches('[data-cart-quantity]')) {
      const value = Number(target.value);
      const min = Number(target.dataset.ruleMin) || 1;
      if (!target.checkValidity() || (value !== 0 && value < min)) {
        showMessage('[data-cart-error]', labels.quantityError);
        if (lastCart) renderCart(lastCart); else target.value = target.defaultValue;
        return;
      }
      changeItem(target.dataset.itemKey, value, target);
    }
  });
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || dialog()?.open) return;
    const menu = q('[data-menu-toggle][aria-expanded="true"]');
    if (menu) { menu.click(); menu.focus(); }
  });
  dialog()?.addEventListener('click', event => { if (event.target === dialog()) closeCart(); });
  dialog()?.addEventListener('close', () => { document.documentElement.classList.remove('sc-cart-open'); previousFocus?.focus(); });
  document.addEventListener('shopify:section:load', event => init(event.target));
  window.addEventListener('pageshow', event => { if (event.persisted) getCart().catch(() => {}); });
  init();
})();
